import { useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Rating from '@mui/material/Rating';
import { useTheme } from '@mui/material/styles';
import { useForkRef } from '@mui/material/utils';
import Tooltip from '@/components/mui/Tooltip';
import SearchCharactersWarning from '@/components/mui/Layouts/components/SearchCharactersWarning';
import CountChip from '@/components/AgGrid/components/TagsCellRenderer/CountChip';

import {
  SearchField,
  FilterChip,
  ChipStrip,
} from '@/pages/company/WorkspaceRoute/components/WorkspaceSearch/StyledComponents';
import OverflowChips from '@/pages/company/WorkspaceRoute/components/WorkspaceSearch/OverflowChips';
import useElementWidth from '@/pages/company/WorkspaceRoute/components/WorkspaceSearch/useElementWidth';
import useChildWidths from '@/pages/company/WorkspaceRoute/components/WorkspaceSearch/useChildWidths';
import { splitOverflow, fitVisibleCount } from '@/pages/company/WorkspaceRoute/components/WorkspaceSearch/chips';
import {
  CHIP_GAP_SPACING,
  INPUT_MIN_SPACING,
  FALLBACK_VISIBLE_CHIPS,
} from '@/pages/company/WorkspaceRoute/components/WorkspaceSearch/constants';
import type { RenderChip } from '@/pages/company/WorkspaceRoute/components/WorkspaceSearch/types';

interface WorkspaceSearchInputProps {
  params: AutocompleteRenderInputParams;
  chips: RenderChip[];
  thresholdWarning: boolean;
  hasActiveFilters: boolean;
  canSaveFavorite: boolean;
  onSaveFavorite: () => void;
  onClearAll: () => void;
}

// The custom "input-shell" rendered as the Autocomplete's `renderInput` target: a rectangular
// TextFieldBase whose start-adornment hosts the search icon + visible chip strip + "+N" overflow,
// and whose end-adornment hosts the Save-to-favorite star and the Clear-all control. The
// below-threshold hint floats underneath instead of sitting in the end adornment — inline it moved
// the width the chip threshold is measured against. Autocomplete drives the value + keyboard nav
// through `params.inputProps` and anchors its suggestions popper via `params.InputProps.ref`.
const WorkspaceSearchInput = ({
  params,
  chips,
  thresholdWarning,
  hasActiveFilters,
  canSaveFavorite,
  onSaveFavorite,
  onClearAll,
}: WorkspaceSearchInputProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const chipGap = Number.parseFloat(theme.spacing(CHIP_GAP_SPACING));
  const inputFloor = Number.parseFloat(theme.spacing(INPUT_MIN_SPACING));
  const isRtl = theme.direction === 'rtl';

  // One of two ResizeObservers here: this one watches the shell (the OutlinedInput root), while
  // `useChildWidths` below watches the hidden measurement layer. The shell's width is the one thing that
  // moves without React hearing about it (window drag, drawer toggle).
  //
  // Two consumers, and the difference matters: the geometry layout effect takes it only as a CHANGE
  // SIGNAL — it re-reads the DOM itself — while the overflow Popover takes it as a VALUE, its paper
  // being exactly this wide to match how Autocomplete sizes its suggestions dropdown.
  //
  // It cannot loop, structurally rather than by luck: the shell's width is imposed by the app-bar flex
  // container and its max-width, and `minWidth: 0` down the chain plus the lane's `overflow: hidden`
  // mean no content this component renders can push it back. The Popover is portaled to
  // `document.body` and absolutely positioned, so sizing it from this value cannot feed back either.
  const [shellRef, shellWidth] = useElementWidth<HTMLDivElement>();
  // The OutlinedInput root already carries Autocomplete's popper-anchor ref — fork so all three get
  // it. `shellNodeRef` is the node itself: the overflow Popover anchors to the SAME element the
  // suggestions dropdown does, instead of to a wrapper that merely lines up with it.
  const shellNodeRef = useRef<HTMLDivElement>(null);
  const inputRef = useForkRef(params.InputProps.ref, shellRef, shellNodeRef);
  const chipLaneRef = useRef<HTMLDivElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const htmlInputRef = useForkRef(params.inputProps.ref, textFieldRef);

  const [measureRef, measuredWidths] = useChildWidths(chips.map((chip) => chip.id).join('|'));
  // Layer children are [...chips, counter], so the counter's own measured width is the reserve.
  const chipWidths = measuredWidths.slice(0, chips.length);
  const counterWidth = measuredWidths[chips.length] ?? 0;

  // Read from the DOM rather than reconstructed from constants — the geometry already knows the icon's
  // width, the adornment paddings and the end adornment's size, and every constant re-deriving them
  // would be one more thing able to drift out of step with the CSS. See the layout effect below.
  const [geometry, setGeometry] = useState<{ available: number | null; hintOffset: number }>({
    available: null,
    hintOffset: 0,
  });

  // `available === null` means "not measured yet". Deliberately NOT `available > 0`: that conflated
  // "no measurement" with a legitimate measurement of "no room at all", and answered the second case
  // with the pre-measurement fallback — painting chips into a zero-width lane instead of collapsing
  // them into "+N". A measured zero or negative belongs in fitVisibleCount, which resolves it to zero
  // chips; the counter sits outside the lane precisely so it survives that state.
  const chipsMeasured = measuredWidths.length === chips.length + 1;
  const visibleCount =
    geometry.available !== null && chipsMeasured
      ? fitVisibleCount(chipWidths, { available: geometry.available, countReserve: counterWidth, gap: chipGap })
      : FALLBACK_VISIBLE_CHIPS;
  const { visible: visibleChips, hidden: hiddenChips } = splitOverflow(chips, visibleCount);

  const shownChipsWidth = chipWidths.slice(0, visibleCount).reduce((sum, width) => sum + width, 0);

  // Two geometric facts, both read after layout has settled, and both taken between LOGICAL edges:
  // `offsetLeft` is physical — a distance from the LEFT edge whatever the writing direction — while the
  // row itself is mirrored in Hebrew (the theme's `direction: rtl` plus stylis-plugin-rtl), so the chip
  // lane sits to the RIGHT of the text field there. Reading the physical pair unconditionally made the
  // span negative in RTL, and fitVisibleCount resolved that to zero visible chips: every chip collapsed
  // under "+N" the moment a filter existed (DA-19733).
  //
  // `available` — how wide the chip lane may grow. Taken as the span from the lane's INLINE-START edge
  // to the text field's INLINE-END edge, minus the field's floor and the gap the start adornment keeps.
  // Both terms are OUTER widths: `offsetWidth` is one by definition, and the floor is one only because
  // StyledComponents forces the input to `border-box` — MUI leaves it `content-box`, where the same
  // token bounded the content box instead and the lane was over-reserved by the input's horizontal
  // padding. That far boundary is stable no matter how many chips show: the field has `flexGrow: 1`, so
  // it absorbs whatever the lane does not use and the edge facing the lane stays put. Measuring the
  // lane's own width instead would be a one-way ratchet — hide a chip, the lane shrinks, and the room
  // that freed up is never seen again (da-19657 design.md → Risks). Because this value does not depend
  // on the visible count, the computation reaches a fixed point after one extra render instead of
  // chasing itself.
  //
  // `hintOffset` — the hint belongs under the TEXT FIELD, not under the input's inline-start edge: with
  // chips present the caret sits well past them, and a hint pinned to the input's edge appears nowhere
  // near where the user is typing. It is applied as `left`, which stylis-plugin-rtl rewrites to `right`
  // for the RTL cache, so the value has to be an INLINE-START offset too or the flip would measure it
  // from the opposite edge. The offsets are against `.MuiOutlinedInput-root` (MUI makes it positioned
  // for the notched outline), which shares both edges with the wrapper below — both are full-width with
  // no margin — so `shell`'s padding-box width converts a physical offset into an inline-start one.
  //
  // No observer for either: they only change as a consequence of values already in state (the shell's
  // width, the shown chips' summed width, and whether the favourite/clear controls are rendered), so
  // each change already arrives with a re-render. Summed width rather than count, because a late web
  // font can widen the chips — moving the field — without changing how many of them fit.
  useLayoutEffect(() => {
    const lane = chipLaneRef.current;
    const field = textFieldRef.current;
    const shell = shellNodeRef.current;
    if (!lane || !field || !shell) return;
    // NOT `Math.abs` of the LTR expression: mirrored, the field's far boundary is its OTHER edge, so an
    // absolute value would measure the gap between two wrong edges.
    const span = isRtl
      ? lane.offsetLeft + lane.offsetWidth - field.offsetLeft
      : field.offsetLeft + field.offsetWidth - lane.offsetLeft;
    const next = {
      available: span - inputFloor - chipGap,
      hintOffset: isRtl ? shell.clientWidth - field.offsetLeft - field.offsetWidth : field.offsetLeft,
    };
    setGeometry((prev) => (prev.available === next.available && prev.hintOffset === next.hintOffset ? prev : next));
  }, [shellWidth, shownChipsWidth, canSaveFavorite, hasActiveFilters, chipGap, inputFloor, isRtl]);

  return (
    // `position: relative` host for the below-threshold hint. The hint is taken OUT of the inline
    // endAdornment flow on purpose: inline it consumed part of the input while the user typed,
    // which both starved the typing area and moved the width the chip threshold is measured
    // against (DA-19687 item 7; da-19657 design.md → Risks).
    <Box sx={{ position: 'relative', width: '100%' }}>
      <SearchField
        fullWidth
        size="small"
        placeholder={formatMessage({ id: 'header.search.placeholder' })}
        slotProps={{
          htmlInput: { ...params.inputProps, ref: htmlInputRef },
          input: {
            ...params.InputProps,
            ref: inputRef,
            startAdornment: (
              <InputAdornment position="start">
                <Stack direction="row" alignItems="center" gap={CHIP_GAP_SPACING} sx={{ minWidth: 0 }}>
                  <SearchIcon color="action" />
                  <ChipStrip ref={chipLaneRef} direction="row" alignItems="center" gap={CHIP_GAP_SPACING}>
                    {visibleChips.map((chip) => (
                      <Tooltip key={chip.id} t={chip.title}>
                        <FilterChip
                          size="small"
                          color="primary"
                          variant="outlined"
                          avatar={chip.avatar}
                          icon={chip.icon}
                          label={chip.label}
                          onDelete={chip.onDelete}
                        />
                      </Tooltip>
                    ))}
                  </ChipStrip>
                  <OverflowChips hiddenChips={hiddenChips} anchorRef={shellNodeRef} surfaceWidth={shellWidth} />
                </Stack>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Stack direction="row" alignItems="center" gap={CHIP_GAP_SPACING}>
                  {canSaveFavorite && (
                    <Tooltip t={{ id: 'documents.tags.saveToFavorites', defaultMessage: 'Save to Favorites' }}>
                      <Rating max={1} value={null} size="small" onChange={() => onSaveFavorite()} />
                    </Tooltip>
                  )}
                  {hasActiveFilters && (
                    <Tooltip t={{ id: 'company.workspace.search.clearAll', defaultMessage: 'Clear all' }}>
                      <IconButton
                        size="small"
                        onClick={onClearAll}
                        aria-label={formatMessage({
                          id: 'company.workspace.search.clearAll',
                          defaultMessage: 'Clear all',
                        })}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </InputAdornment>
            ),
          },
        }}
      />
      {thresholdWarning && (
        // Elevated so the hint reads as a floating message belonging to the input rather than as a
        // stray page element: it sits on the app-bar/content seam, where the shared warning chip's
        // pale fill alone is easy to miss. The shared chip itself is left untouched — the
        // companies-list search renders it inline, where it needs no surface.
        <Paper
          elevation={3}
          sx={{ position: 'absolute', top: '100%', left: geometry.hintOffset, mt: 0.5, p: 0.5, borderRadius: 1 }}
        >
          <SearchCharactersWarning />
        </Paper>
      )}
      {/* Hidden measurement layer — see useChildWidths. `width: max-content` + nowrap so the chips
          lay out at their intrinsic (cap-truncated) width instead of being flex-shrunk to the
          wrapper. `onDelete` is the real handler so the delete icon's width counts; it is
          unreachable anyway (hidden, pointer-events none). */}
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          display: 'flex',
          flexWrap: 'nowrap',
          width: 'max-content',
          gap: CHIP_GAP_SPACING,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {chips.map((chip) => (
          <FilterChip
            key={chip.id}
            size="small"
            color="primary"
            variant="outlined"
            avatar={chip.avatar}
            icon={chip.icon}
            label={chip.label}
            onDelete={chip.onDelete}
          />
        ))}
        {/* The widest "+N" this filter set can produce (every chip hidden). Measured rather than
            guessed: a constant would have to assume a digit count, and large filter configs are
            realistic. `chips.length` does not depend on how many chips are shown, so using it keeps
            the budget independent of its own result. */}
        <CountChip size="small" label={chips.length} />
      </Box>
    </Box>
  );
};

WorkspaceSearchInput.displayName = 'WorkspaceSearchInput';

export default WorkspaceSearchInput;
